import { useCallback } from 'react';

import { arrayHasLength } from '@givebacks/utils/array';
import { ClickableOptionsCard } from 'core/Components';
import { centsToDollar } from 'core/Utils';
import { useListUserActiveSubscriptions } from 'store/Api';
import { Button, Modal, NoDataMessageCard, Skeleton, Stack } from 'ui';

import {
  ModalTitleWithBackButton,
  useDonationSettingModalProvider,
} from '../DonationSettingsModalProvider';

const ManageRecurringDonationsModalContent = ({
  handleClick,
}: {
  handleClick: (uuid: string) => void;
}) => {
  const { data: userSubscriptions, isLoading } = useListUserActiveSubscriptions();

  if (!arrayHasLength(userSubscriptions) && !isLoading)
    return (
      <NoDataMessageCard
        title="No Recurring Donations"
        message="When you make a recurring donation, you can view, edit, and cancel the donation here."
      />
    );

  return (
    <Skeleton height={72} visible={isLoading}>
      <Stack>
        {userSubscriptions?.map((subscription) => (
          <ClickableOptionsCard key={subscription.uuid}>
            <ClickableOptionsCard.Row
              title={subscription.cause.metadata?.name}
              subtitle={`${centsToDollar(subscription.price)} / month`}
              onClick={() => handleClick(subscription.uuid)}
              rightSection={
                <Button size="sm" variant="subtle" component="div">
                  Edit
                </Button>
              }
              // Out of scope, data does not exist
              // infoBottom={`Next Bill Date: ${DateTime.fromISO(subscription.created_at).toLocaleString(
              //   'MM/dd/yy'
              // )}`}
            />
          </ClickableOptionsCard>
        ))}
      </Stack>
    </Skeleton>
  );
};

const ManageRecurringDonationsModal = () => {
  const { opened, close, setMeta, openModalById } = useDonationSettingModalProvider({
    id: 'ManageRecurring',
  });

  const handleEdit = useCallback(
    (uuid: string) => {
      setMeta((prev) => ({
        ...prev,
        subscriptionId: uuid,
      }));
      openModalById('EditRecurring');
    },
    [openModalById, setMeta]
  );

  return (
    <Modal
      opened={opened}
      onClose={close}
      title={<ModalTitleWithBackButton>Manage Recurring Donations</ModalTitleWithBackButton>}
    >
      <ManageRecurringDonationsModalContent handleClick={handleEdit} />
    </Modal>
  );
};

export { ManageRecurringDonationsModal, ManageRecurringDonationsModalContent };
